import { externalMapLink } from "../../../geo/service";
import React, { useState, useEffect } from "react";
import {
  Modal,
  Typography,
  Descriptions,
  Button,
  Row,
  Col,
  Divider,
  Tag,
} from "antd";
import PropTypes from "prop-types";
import {
  UserOutlined,
  FileTextOutlined,
  MedicineBoxOutlined,
  HomeOutlined,
  CommentOutlined,
  ContactsOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { Title } = Typography;

// Rango amplio de Colombia (incluye San Andrés y Leticia), el mismo que valida el backend
const enColombia = (lat, lng) => lat >= -4.5 && lat <= 13.6 && lng >= -82 && lng <= -66.5;

const formatearFechaHora = (valor) => {
  if (!valor) return "N/A";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return valor;
  return fecha.toLocaleString("es-CO", { timeZone: "America/Bogota", dateStyle: "short", timeStyle: "short" });
};

const textoLargoStyle = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" };

const ViewPatientDialog = ({ patient, open, handleClose }) => {
  const [edad, setEdad] = useState(""); // Estado para la edad calculada

  useEffect(() => {
    setEdad(patient?.fecha_nacimiento ? calculateAge(patient.fecha_nacimiento) : "");
  }, [patient]);

  const calculateAge = (fechaNacimiento) => {
    const [anio, mes, dia] = String(fechaNacimiento).slice(0, 10).split("-").map(Number);
    const birthDate = new Date(anio, mes - 1, dia);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const coordenadasTexto = (coordenadas) => {
    if (!coordenadas?.coordinates) return null;
    const [longitude, latitude] = coordenadas.coordinates;
    return { texto: `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`, valida: enColombia(latitude, longitude) };
  };
  const coords = coordenadasTexto(patient.coordenadas);
  const responsables = [...(patient.responsables || [])].sort(
    (a, b) => Number(Boolean(b.es_principal)) - Number(Boolean(a.es_principal)) || (a.orden ?? 0) - (b.orden ?? 0)
  );

  const googleMapsLink = (coordenadas) => {
    if (coordenadas?.coordinates) {
      const [longitude, latitude] = coordenadas.coordinates;
      // Google Maps espera formato lat,lng
      return externalMapLink(latitude,longitude);
    }
    return "#";
  };

  const googleStreetViewLink = (coordenadas) => {
    if (coordenadas?.coordinates) {
      const [longitude, latitude] = coordenadas.coordinates;
      // Google Street View espera formato lat,lng
      return externalMapLink(latitude,longitude,'street_view');
    }
    return "#";
  };

  // Estilos personalizados para títulos y descripciones
  const titleStyle = {
    background: "#0a2e5c",
    color: "white",
    padding: "6px",
    borderRadius: "4px",
    fontSize: "14px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
  };

  const descriptionItemStyle = {
    marginBottom: "2px",
  };

  const labelStyle = {
    fontWeight: "bold",
    fontSize: "12px",
  };

  const contentStyle = {
    fontSize: "12px",
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      width={1200} // Aumentado para acomodar tres columnas
      title={
        <div
          style={{
            background: "linear-gradient(90deg, #0a2e5c, #004d99)",
            padding: "10px",
            color: "white",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Title
            level={5}
            style={{ color: "white", margin: 0, fontSize: "16px" }}
          >
            <UserOutlined style={{ marginRight: "8px" }} />
            Ver Paciente
          </Title>
        </div>
      }
      footer={[
        <Button key="close" type="primary" onClick={handleClose}>
          Cerrar
        </Button>,
      ]}
      style={{ maxHeight: "80vh", overflow: "auto" }} // Limitar la altura máxima del modal y permitir scroll si es necesario
      destroyOnHidden
    >
      <div style={{ padding: "12px" }}>
        <Row gutter={[8, 8]}>
          {/* Columna 1: Información Personal y Datos de Contacto */}
          <Col span={8}>
            {/* Información Personal */}
            <Title level={5} style={titleStyle}>
              <FileTextOutlined style={{ marginRight: "6px" }} />
              Información Personal
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
              style={{ marginBottom: "12px" }}
            >
              <Descriptions.Item label="Nombre" style={descriptionItemStyle}>
                {patient.nombre}
              </Descriptions.Item>
              <Descriptions.Item
                label="Tipo de Documento"
                style={descriptionItemStyle}
              >
                {patient.tipo_documento_descripcion || "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Número de Documento"
                style={descriptionItemStyle}
              >
                {patient.numero_documento}
              </Descriptions.Item>
              <Descriptions.Item label="Género" style={descriptionItemStyle}>
                {patient.genero}
              </Descriptions.Item>
              <Descriptions.Item
                label="Fecha de Nacimiento"
                style={descriptionItemStyle}
              >
                {patient.fecha_nacimiento || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Edad" style={descriptionItemStyle}>
                {edad !== "" ? `${edad} años` : "N/A"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Datos de Contacto */}
            <Title level={5} style={titleStyle}>
              <ContactsOutlined style={{ marginRight: "6px" }} />
              Datos de Contacto
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
              style={{ marginBottom: "12px" }}
            >
              <Descriptions.Item label="Teléfono" style={descriptionItemStyle}>
                {patient.telefono || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Correo Electrónico"
                style={descriptionItemStyle}
              >
                {patient.correo || "N/A"}
              </Descriptions.Item>
            </Descriptions>

            <Divider />

            {/* Registro */}
            <Title level={5} style={titleStyle}>
              <ClockCircleOutlined style={{ marginRight: "6px" }} />
              Registro
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
              style={{ marginBottom: "12px" }}
            >
              <Descriptions.Item label="Fecha de Registro" style={descriptionItemStyle}>
                {formatearFechaHora(patient.createdAt || patient.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Última Actualización" style={descriptionItemStyle}>
                {formatearFechaHora(patient.updatedAt || patient.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          {/* Columna 2: Domicilio e Información Médica */}
          <Col span={8}>
            {/* Domicilio */}
            <Title level={5} style={titleStyle}>
              <HomeOutlined style={{ marginRight: "6px" }} />
              Domicilio
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
              style={{ marginBottom: "12px" }}
            >
              <Descriptions.Item label="Zona" style={descriptionItemStyle}>
                {patient.Zona?.nombre || patient.zona_nombre || "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Departamento"
                style={descriptionItemStyle}
              >
                {patient.Ciudad?.Departamento?.nombre ||
                  patient.departamento_nombre ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Ciudad/Municipio"
                style={descriptionItemStyle}
              >
                {patient.Ciudad?.nombre ||
                  patient.ciudad_nombre ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item label="Localidad" style={descriptionItemStyle}>
                {patient.localidad || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Barrio" style={descriptionItemStyle}>
                {patient.barrio || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Vereda/Corregimiento" style={descriptionItemStyle}>
                {patient.vereda_corregimiento || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Dirección" style={descriptionItemStyle}>
                {patient.direccion || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Indicaciones"
                style={descriptionItemStyle}
              >
                <span style={textoLargoStyle}>{patient.indicaciones || "N/A"}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Coordenadas" style={descriptionItemStyle}>
                {coords ? (
                  <>
                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                    {coords.texto}
                    {!coords.valida && (
                      <Tag color="red" style={{ marginLeft: 6 }}>
                        Fuera de Colombia: revisar
                      </Tag>
                    )}
                  </>
                ) : (
                  <Tag color="orange">Sin coordenadas</Tag>
                )}
              </Descriptions.Item>
            </Descriptions>
            {coords && (
              <div style={{ marginBottom: "12px" }}>
                <Button
                  type="primary"
                  href={googleMapsLink(patient.coordenadas)}
                  target="_blank"
                  style={{ marginRight: "8px", marginBottom: "4px" }}
                >
                  Ver en Google Maps
                </Button>
                <Button
                  type="default"
                  href={googleStreetViewLink(patient.coordenadas)}
                  target="_blank"
                >
                  Ver en Street View
                </Button>
              </div>
            )}

            <Divider />

            {/* Información Médica */}
            <Title level={5} style={titleStyle}>
              <MedicineBoxOutlined style={{ marginRight: "6px" }} />
              Información Médica
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
            >
              <Descriptions.Item label="EPS" style={descriptionItemStyle}>
                {patient.Cliente?.nombre_cliente ||
                  patient.eps_nombre ||
                  patient.eps ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item label="Régimen" style={descriptionItemStyle}>
                {patient.Regimen?.nombre ||
                  patient.regimen_nombre ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Afiliación"
                style={descriptionItemStyle}
              >
                {patient.Afiliacion?.nombre ||
                  patient.afiliacion_nombre ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Diagnóstico"
                style={descriptionItemStyle}
              >
                {patient.Diagnostico?.nombre ||
                  patient.diagnostico_nombre ||
                  "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo" style={descriptionItemStyle}>
                {patient.Tipo?.nombre || patient.tipo_nombre || "Desconocido"}
              </Descriptions.Item>
              <Descriptions.Item label="Estado" style={descriptionItemStyle}>
                {patient.Estado?.descripcion ||
                  patient.estado_descripcion ||
                  "Desconocido"}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          {/* Columna 3: Responsables y Observaciones */}
          <Col span={8}>
            {/* Responsables */}
            <Title level={5} style={titleStyle}>
              <TeamOutlined style={{ marginRight: "6px" }} />
              Responsables
            </Title>

            {/* Mostrar lista de responsables si existe */}
            {responsables.length > 0 ? (
              responsables.map((responsable, index) => (
                <Descriptions
                  key={responsable.id || index}
                  column={1}
                  bordered
                  size="small"
                  labelStyle={labelStyle}
                  contentStyle={contentStyle}
                  style={{ marginBottom: "8px" }}
                  title={
                    <span style={{ fontSize: "11px", color: responsable.es_principal ? "#1890ff" : "#666" }}>
                      {responsable.es_principal ? "Principal" : `Responsable ${index + 1}`}
                    </span>
                  }
                >
                  <Descriptions.Item
                    label="Nombre"
                    style={descriptionItemStyle}
                  >
                    {responsable.nombre}
                  </Descriptions.Item>
                  {(responsable.tipo_documento_id || responsable.numero_documento) && (
                    <Descriptions.Item
                      label="Documento"
                      style={descriptionItemStyle}
                    >
                      {[responsable.tipo_documento_descripcion, responsable.numero_documento].filter(Boolean).join(": ") || "N/A"}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item
                    label="Parentesco"
                    style={descriptionItemStyle}
                  >
                    {responsable.parentesco || "N/A"}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label="Teléfono"
                    style={descriptionItemStyle}
                  >
                    {responsable.telefono || "N/A"}
                  </Descriptions.Item>
                </Descriptions>
              ))
            ) : !patient.nombre_responsable && !patient.telefono_responsable ? (
              <Tag color="default" style={{ marginBottom: "12px" }}>Sin responsables registrados</Tag>
            ) : (
              /* Fallback a campos planos si no hay responsables en array */
              <Descriptions
                column={1}
                bordered
                size="small"
                labelStyle={labelStyle}
                contentStyle={contentStyle}
                style={{ marginBottom: "12px" }}
              >
                <Descriptions.Item
                  label="Nombre del Responsable"
                  style={descriptionItemStyle}
                >
                  {patient.nombre_responsable}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Parentesco"
                  style={descriptionItemStyle}
                >
                  {patient.parentesco_responsable}
                </Descriptions.Item>
                <Descriptions.Item
                  label="Teléfono del Responsable"
                  style={descriptionItemStyle}
                >
                  {patient.telefono_responsable}
                </Descriptions.Item>
              </Descriptions>
            )}

            <Divider />

            {/* Observaciones */}
            <Title level={5} style={titleStyle}>
              <CommentOutlined style={{ marginRight: "6px" }} />
              Observaciones
            </Title>
            <Descriptions
              column={1}
              bordered
              size="small"
              labelStyle={labelStyle}
              contentStyle={contentStyle}
            >
              <Descriptions.Item
                label="Observaciones"
                style={descriptionItemStyle}
              >
                <span style={textoLargoStyle}>{patient.observaciones || "N/A"}</span>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

// Validación de las props con PropTypes
ViewPatientDialog.propTypes = {
  patient: PropTypes.shape({
    nombre: PropTypes.string.isRequired,
    TipoDocumento: PropTypes.shape({
      descripcion: PropTypes.string,
    }),
    numero_documento: PropTypes.string.isRequired,
    genero: PropTypes.string,
    fecha_nacimiento: PropTypes.string,
    vereda_corregimiento: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    responsables: PropTypes.arrayOf(PropTypes.object),
    Zona: PropTypes.shape({
      nombre: PropTypes.string,
    }),
    Ciudad: PropTypes.shape({
      nombre: PropTypes.string,
      Departamento: PropTypes.shape({
        nombre: PropTypes.string,
      }),
    }),
    localidad: PropTypes.string,
    barrio: PropTypes.string,
    direccion: PropTypes.string,
    telefono: PropTypes.string,
    correo: PropTypes.string,
    Diagnostico: PropTypes.shape({
      nombre: PropTypes.string,
    }),
    Afiliacion: PropTypes.shape({
      nombre: PropTypes.string,
    }),
    Regimen: PropTypes.shape({
      nombre: PropTypes.string,
    }),
    Tipo: PropTypes.shape({
      nombre: PropTypes.string,
    }),
    Estado: PropTypes.shape({
      descripcion: PropTypes.string,
    }),
    Cliente: PropTypes.shape({
      nombre_cliente: PropTypes.string,
    }),
    nombre_responsable: PropTypes.string,
    parentesco_responsable: PropTypes.string,
    telefono_responsable: PropTypes.string,
    coordenadas: PropTypes.shape({
      type: PropTypes.string,
      coordinates: PropTypes.arrayOf(PropTypes.number),
    }),
    observaciones: PropTypes.string,
    indicaciones: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewPatientDialog;
